import React, { useEffect, useState } from "react";
import { CreateUserData } from "../../store/types";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useCompaniesAPI } from "../../hooks/api/use-companiesAPI";
import { validateField, formatPhone } from "../../hooks/validation";
import { useGetQuery } from "../../store/slices/apiSlice";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import CustomDropdown from "../../components/CustomInput/CustomDropdown";
import CustomInputMask from "../../components/CustomInput/CustomInputMask";
import { StyledIconLine } from "../../components/StyledIcon/StyledIcon";
import {
  IconArrowLeft,
  IconUser,
  IconCompany,
  IconMail,
  IconPhone,
  IconUserFilled,
  IconCompanyFilled,
  IconMailFilled,
  IconPhoneFilled,
} from "../../assets/icons/Icon";
import { H2, DangerText } from "../../styles/Typography";
import { Container } from "../../components/Container/Container";
import {
  Flex,
  ButtonLabel,
  GoBackButton,
  ButtonContainer,
  Spinner,
} from "./CRUDPages.styles";

const UserFormPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("id");
  const { companyId } = useParams<{ companyId: string }>();

  const isMobile = useMediaQuery("(max-width: 768px)");
  const navigate = useNavigate();
  const mode = searchParams.get("mode") || "create";

  const [loading, setLoading] = useState(false);
  const [companiesOpt, setCompaniesOpt] = useState<
    { label: string; value: any }[]
  >([]);

  const {
    usersEP,
    handlePostUser,
    handleUpdateUser,
    handleDeleteUser,
    refetchData,
    toastRef,
  } = useUsersAPI();
  const { companies } = useCompaniesAPI();

  const [formData, setFormData] = useState<CreateUserData>({
    lastName: "",
    name: "",
    patronymic: "",
    email: "",
    phone: "",
    organizationId: 0,
  });
  const [errors, setErrors] = useState<Record<keyof CreateUserData, boolean>>({
    lastName: false,
    name: false,
    patronymic: false,
    email: false,
    phone: false,
    organizationId: false,
  });
  const [validForm, setValidForm] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);

  const {
    data: user,
    refetch: refetchUser,
    isLoading,
  } = useGetQuery(`${usersEP}/${userId}`, {
    refetchOnMountOrArgChange: true,
    skip: !userId,
  });

  useEffect(() => {
    if (mode !== "create" && isLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [user, isLoading]);

  useEffect(() => {
    if (mode !== "create" && userId && user) {
      const newData = {
        lastName: user.lastName || "",
        name: user.name || "",
        patronymic: user.patronymic || "",
        email: user.email || "",
        phone: user.phone || "",
        organizationId: user.organizationId ?? 0,
      };
      setFormData(newData);
      const newErrors: Record<keyof CreateUserData, boolean> = {
        lastName: !validateField("lastName", newData.lastName),
        name: !validateField("name", newData.name),
        patronymic: !validateField("patronymic", newData.patronymic),
        email: !validateField("email", newData.email),
        phone: !validateField("phone", newData.phone),
        organizationId: !validateField(
          "organizationId",
          newData.organizationId
        ),
      };
      setErrors(newErrors);
    } else {
      setFormData({
        lastName: "",
        name: "",
        patronymic: "",
        email: "",
        phone: "",
        organizationId: Number(companyId) ?? 0,
      });
      setErrors({
        lastName: false,
        name: false,
        patronymic: false,
        email: false,
        phone: false,
        organizationId: false,
      });
    }
  }, [userId, mode, user]);

  useEffect(() => {
    if (companies) {
      const formatted = companies.map((company) => ({
        label: company.name,
        value: company.id,
      }));
      setCompaniesOpt(formatted);
    } else {
      setCompaniesOpt([]);
    }
  }, [companies]);

  useEffect(() => {
    if (isMobile === null) return;

    if (!isMobile) {
      if (companyId) navigate(`/companies/${companyId}`, { replace: true });
      else navigate("/users", { replace: true });
    }
  }, [isMobile, navigate]);

  const handleFieldChange = (field: keyof CreateUserData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateAllFields = (): boolean => {
    const newErrors: Record<keyof CreateUserData, boolean> = {
      lastName: !validateField("lastName", formData.lastName),
      name: !validateField("name", formData.name),
      patronymic: !validateField("patronymic", formData.patronymic),
      email: !validateField("email", formData.email),
      phone: !validateField("phone", formData.phone),
      organizationId: !validateField("organizationId", formData.organizationId),
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((err) => !err);
  };

  const handleBlur = (field: keyof CreateUserData) => {
    const value = formData[field];
    const isValid = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: !isValid }));
  };

  const handleSubmit = async () => {
    if (mode !== "delete")
      if (!validateAllFields()) {
        setValidForm(false);
        return;
      }

    setValidForm(true);
    setSubmitting(true);
    let success = false;
    let response;

    try {
      if (mode === "create") {
        response = await handlePostUser(formData);
        success = response.success;
      } else if (mode === "edit") {
        success = await handleUpdateUser({ ...formData, id: user!.id });
      } else if (mode === "delete") {
        success = await handleDeleteUser(user!);
      }
      if (success) {
        refetchData();
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else return;
    } finally {
      setSubmitting(false);
    }
  };

  const getHeader = () => {
    switch (mode) {
      case "create":
        return "Новый пользователь";
      case "edit":
        return "Редактировать пользователя";
      case "delete":
        return "Удалить пользователя";
      default:
        return "Ошибка";
    }
  };

  const renderForm = () => (
    <>
      <CustomInputText
        value={formData.lastName}
        setValue={(val) => handleFieldChange("lastName", val)}
        onBlur={() => handleBlur("lastName")}
        placeholder="Фамилия"
        icon={<IconUser size={20} />}
        invalid={errors.lastName}
      />
      <CustomInputText
        value={formData.name}
        setValue={(val) => handleFieldChange("name", val)}
        onBlur={() => handleBlur("name")}
        placeholder="Имя"
        icon={<IconUser size={20} />}
        invalid={errors.name}
      />
      <CustomInputText
        value={formData.patronymic}
        setValue={(val) => handleFieldChange("patronymic", val)}
        onBlur={() => handleBlur("patronymic")}
        placeholder="Отчество"
        icon={<IconUser size={20} />}
        invalid={errors.patronymic}
      />
      {companyId ? (
        <></>
      ) : (
        <CustomDropdown
          value={formData.organizationId}
          onChange={(val) => handleFieldChange("organizationId", val)}
          onBlur={() => handleBlur("organizationId")}
          options={companiesOpt}
          placeholder="Компания"
          icon={<IconCompany size={20} />}
          invalid={errors.organizationId}
        />
      )}
      <CustomInputText
        value={formData.email}
        setValue={(val) => handleFieldChange("email", val)}
        onBlur={() => handleBlur("email")}
        placeholder="Почта"
        icon={<IconMail size={20} />}
        invalid={errors.email}
      />
      <CustomInputMask
        value={formData.phone}
        setValue={(val) => handleFieldChange("phone", val)}
        onBlur={() => handleBlur("phone")}
        mask="+7 (999) 999-99-99"
        unmask={true}
        placeholder="+7 (___) ___-__-__"
        icon={<IconPhone size={20} />}
        invalid={errors.phone}
      />
    </>
  );

  const renderDelete = () => (
    <>
      <DangerText>
        Вы уверены, что хотите удалить данного пользователя?
      </DangerText>
      <StyledIconLine
        icon={<IconUserFilled size={20} />}
        text={
          user
            ? `${user.lastName ?? ""} ${user.name ?? ""} ${
                user.patronymic ?? ""
              }`
            : "Нет данных"
        }
      />
      <StyledIconLine
        icon={<IconCompanyFilled size={20} />}
        text={user?.organizationName ?? "Нет данных"}
      />
      <StyledIconLine
        icon={<IconMailFilled size={20} />}
        text={user?.email ?? "Нет данных"}
      />
      <StyledIconLine
        icon={<IconPhoneFilled size={20} />}
        text={
          user
            ? formatPhone(user.phone) == ""
              ? "Нет данных"
              : formatPhone(user.phone)
            : "Нет данных"
        }
      />
    </>
  );

  if (loading)
    return (
      <Container>
        <Spinner>
          <ProgressSpinner />
          <p>Загрузка...</p>
        </Spinner>
      </Container>
    );

  return (
    <Container>
      <Toast ref={toastRef} position="bottom-center" />
      <H2>{getHeader()}</H2>
      {validForm ? (
        <></>
      ) : (
        <DangerText>Пожалуйста, заполните все поля данных.</DangerText>
      )}
      {mode === "delete" ? renderDelete() : renderForm()}
      <Flex>
        <GoBackButton>
          <Button outlined onClick={() => navigate(-1)}>
            <ButtonLabel>
              <IconArrowLeft size={16} />
              Назад
            </ButtonLabel>
          </Button>
        </GoBackButton>
        <ButtonContainer>
          <Button
            label={
              mode === "delete"
                ? "Удалить"
                : mode === "edit"
                ? "Сохранить"
                : "Создать"
            }
            loading={submitting}
            onClick={handleSubmit}
          />
        </ButtonContainer>
      </Flex>
    </Container>
  );
};

export default UserFormPage;
